/**
 * GRANSKARE FÖR PRENUMERATIONEN — de fyra rutterna kring betalningen.
 *
 *   SUBSCRIPTION      läget, ankaret och det preliminära talet
 *   BILLING_CHECKOUT  adressen till Stripes kassa
 *   BILLING_PORTAL    adressen till Stripes betalningssida
 *   BILLING_PERIODS   de stängda ankarperioderna
 *
 * EGEN KOPIA, precis som projektet redan gör på fem ställen (arbetsorder,
 * konto, laget, lagetidag, tidrapport). Hjälparna står i api/granska.ts och
 * returtypen är KONTRAKTETS typ — läggs ett fält till i SubscriptionResponse
 * faller tsc HÄR och inte i drift.
 *
 * TVÅ SAKER SOM PRÖVAS HÅRDARE ÄN KONTRAKTET SKRIVER UT
 *
 *   URL:EN. Kontraktet typar BillingCheckoutResponse.url som `string`, men det
 *   här är den enda strängen i hela klienten som blir en NAVIGERING —
 *   Prenumeration.tsx skickar telefonen dit med location.assign. En
 *   'javascript:'-adress i det fältet vore ett skript som kör i vår egen
 *   origin med kontots bevis i valvet. Protokollet prövas därför här, vid
 *   nätgränsen, där varje annan form redan prövas. Serverns egna adresser är
 *   alltid https (Stripes checkout.stripe.com / billing.stripe.com), så
 *   spärren kostar ingenting i drift.
 *
 *   ANTALEN. `heltal` släpper igenom negativa tal. Ett negativt
 *   current_billed_user_count eller billed_user_count skulle bli ett negativt
 *   belopp på skärmen — alltså "firman får pengar tillbaka" — och det är
 *   hellre ett granskningsfel än en siffra chefen ringer om.
 *
 * PRENUMERATIONSLAGEN LÅNAS UR granskare/konto.ts och skrivs INTE av här.
 * Listan är uttömmande mot SubscriptionStatus (raden UttommandePrenumerationslagen
 * bevisar det), och en andra kopia hade kunnat glida ifrån den första.
 */

import type {
  BillingCheckoutResponse,
  BillingPeriodRow,
  BillingPeriodsResponse,
  BillingPortalResponse,
  SubscriptionPublicRow,
  SubscriptionResponse,
} from '@delat/types';
import {
  Granskningsfel,
  companyId,
  enAv,
  heltal,
  lista,
  millisekunder,
  nullbar,
  objekt,
  oren,
  text,
} from '../granska';
import type { Granskare } from '../granska';
import { PRENUMERATIONSLAGEN } from './konto';

/** Ett antal personer. Aldrig negativt — se filhuvudet. */
const antal = (varde: unknown, falt: string): number => {
  const n = heltal(varde, falt);
  if (n < 0) throw new Granskningsfel(falt, 'ett antal personer kan inte vara negativt');
  return n;
};

/** En adress vi skickar telefonen till. Bara http och https — se filhuvudet. */
const betalningsadress = (varde: unknown, falt: string): string => {
  const s = text(varde, falt);
  let adress: URL;
  try {
    adress = new URL(s);
  } catch {
    throw new Granskningsfel(falt, 'förväntade en fullständig adress');
  }
  if (adress.protocol !== 'https:' && adress.protocol !== 'http:') {
    throw new Granskningsfel(falt, `adressen bär protokollet ${adress.protocol}`);
  }
  return s;
};

/**
 * SubscriptionPublicRow. De tre stripe_*-kolumnerna finns INTE i typen och
 * läses därför inte heller här: firmans betalningsidentitet lämnar aldrig
 * servern (beslut 07), och bokföraren står i FULL_VIEW_ROLES.
 * current_period_end är firmans ANKARE (beslut 18) — både nästa dragning och
 * gränsen mellan två perioder. Det finns inget separat datumfält.
 */
export const granskaPrenumerationsrad: Granskare<SubscriptionPublicRow> = (v) => {
  const o = objekt(v, 'subscription');
  return {
    company_id: companyId(o['company_id'], 'subscription.company_id'),
    status: enAv(o['status'], 'subscription.status', PRENUMERATIONSLAGEN),
    current_period_start: millisekunder(
      o['current_period_start'],
      'subscription.current_period_start',
    ),
    current_period_end: millisekunder(
      o['current_period_end'],
      'subscription.current_period_end',
    ),
    updated_at: millisekunder(o['updated_at'], 'subscription.updated_at'),
  };
};

export const granskaPrenumeration: Granskare<SubscriptionResponse> = (v) => {
  const o = objekt(v, 'subscription_response');
  return {
    subscription: granskaPrenumerationsrad(o['subscription']),
    // Beslut 18: antal UPPLAGDA med en betald roll just nu, inte antal som
    // rapporterat något. Talet räknas av servern med exakt samma fråga som
    // stängningen, och klienten räknar det aldrig själv.
    current_billed_user_count: antal(
      o['current_billed_user_count'],
      'current_billed_user_count',
    ),
    price_per_user_ore: oren(o['price_per_user_ore'], 'price_per_user_ore'),
  };
};

export const granskaKassalank: Granskare<BillingCheckoutResponse> = (v) => {
  const o = objekt(v, 'billing_checkout');
  return { url: betalningsadress(o['url'], 'billing_checkout.url') };
};

export const granskaPortallank: Granskare<BillingPortalResponse> = (v) => {
  const o = objekt(v, 'billing_portal');
  return { url: betalningsadress(o['url'], 'billing_portal.url') };
};

/**
 * EN STÄNGD ANKARPERIOD. stripe_invoice_id är det ENDA Stripe-id som lämnar
 * servern (beslut 07): det pekar ut en enskild faktura och inte firmans
 * kundförhållande, och det är just det talet bokföraren behöver för att stämma
 * av Klartids period mot bokföringen. null betyder stängd men ännu inte betald.
 */
const granskaPeriod: Granskare<BillingPeriodRow> = (v) => {
  const o = objekt(v, 'periods[]');
  return {
    company_id: companyId(o['company_id'], 'periods[].company_id'),
    period_start: millisekunder(o['period_start'], 'periods[].period_start'),
    period_end: millisekunder(o['period_end'], 'periods[].period_end'),
    billed_user_count: antal(o['billed_user_count'], 'periods[].billed_user_count'),
    amount_ore: oren(o['amount_ore'], 'periods[].amount_ore'),
    closed_at: millisekunder(o['closed_at'], 'periods[].closed_at'),
    stripe_invoice_id: nullbar(o['stripe_invoice_id'], 'periods[].stripe_invoice_id', (x) =>
      text(x, 'periods[].stripe_invoice_id'),
    ),
  };
};

export const granskaFaktureringsperioder: Granskare<BillingPeriodsResponse> = (v) => {
  const o = objekt(v, 'billing_periods');
  return { periods: lista(o['periods'], 'periods', granskaPeriod) };
};
